import React from 'react';
import { InstagramIcon, MailIcon, MessageCircleIcon, UsersIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { ButtonExternal } from '../components/ui/Button';
import { NewTabHint } from '../components/ui/NewTabHint';
import { Reveal, stagger } from '../components/motion/Reveal';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { emailAddress, externalLinks } from '../data/site';
const contactCards = [{
  icon: MailIcon,
  label: 'Email',
  value: emailAddress,
  href: externalLinks.email
}, {
  icon: InstagramIcon,
  label: 'Instagram',
  value: '@ucfbcm',
  href: externalLinks.instagram
}, {
  icon: UsersIcon,
  label: 'Discord',
  value: 'Join the UCF BCM Discord',
  href: externalLinks.discord
}, {
  icon: MessageCircleIcon,
  label: 'Official UCF Organization Page',
  value: 'View UCF BCM on KnightConnect',
  href: externalLinks.knightConnect
}];
const visitingDetails = [{
  term: 'When',
  detail: 'Thursdays at 7:30 PM'
}, {
  term: 'Location',
  detail: 'Cape Florida Ballroom, Room 316, UCF Student Union third floor'
}];
export function Connect() {
  return <>
      <PageHeader eyebrow="Connect" title="Let’s get connected" intro="Interested in a community group, planning your first Thursday, or simply have a question? Share your information and a BCM leader can follow up." data-id="element-443" data-mp="2fbnzhil52n42" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="form-heading" data-id="element-444" data-mp="3801ya0k1xngo">
        <DoodleScatter tone="white" seed="connect-form" data-id="element-445" data-mp="2tbf8f4wstdb7" />
        <Container className="relative grid items-start gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12" data-id="element-446" data-mp="2ya8q79j4bqh6">
          <div className="rounded-[24px] border-2 border-brand-navy bg-white p-6 sm:p-8" data-id="element-447" data-mp="3m02yddshsqv3">
            <h2 id="form-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-3xl" data-id="element-448" data-mp="3qrl229lwh5jr">
              Tell us how we can help
            </h2>
            <p className="mt-3 font-sans text-[16px] leading-relaxed text-ink" data-id="element-449" data-mp="3vp05kki6tfwr">
              The Get Connected form goes straight to the BCM leadership team. If the embedded form
              below does not load in your browser, use the button to open it in a new tab.
            </p>
            <ButtonExternal href={externalLinks.getConnectedForm} className="mt-6" data-id="element-450" data-mp="3hoh0nm90qpyt">
              Open the Get Connected Form
            </ButtonExternal>

            <div className="mt-7 overflow-hidden rounded-[20px] border-2 border-brand-navy bg-brand-tan" data-id="element-451" data-mp="3cve73e48rgim">
              <iframe src={`${externalLinks.getConnectedForm}?embedded=true`} title="UCF BCM Get Connected form" loading="lazy" className="h-[520px] w-full border-0 bg-white" data-id="element-452" data-mp="37w1fyh6c7na8">
                Loading the Get Connected form…
              </iframe>
            </div>
            <p className="mt-3 font-meta text-[12.5px] text-ink" data-id="element-453" data-mp="19fivk4grw9rk">
              The form is hosted by Google. Nothing you type is stored by this website.
            </p>
          </div>

          <div data-id="element-454" data-mp="1igqw6t6rhmot">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-455" data-mp="1iibqchp5z3m5">Other ways to reach us</h2>
            <ul className="mt-5 space-y-4" data-id="element-456" data-mp="1nf23ox9l3l3v">
              {contactCards.map((card, index) => <Reveal key={card.label} as="li" delay={stagger(index)} className="rounded-[20px] border-2 border-brand-navy bg-brand-tan p-5 transition-transform duration-200 hover:-translate-y-1" data-id="element-457" data-mp="qrdx9civxh05" data-mpi={index}>
                  <div className="flex items-start gap-3" data-id="element-458" data-mp="14ij1krx76xgt">
                    <card.icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-navy" data-id="element-459" data-mp="723ic0qyr033" />
                    <div className="min-w-0" data-id="element-460" data-mp="c16nrmauv8bl">
                      <p className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-461" data-mp="bzvjlldhzyjy">
                        {card.label}
                      </p>
                      <a href={card.href} {...card.href.startsWith('mailto:') ? {} : {
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  }} className="mt-1 inline-block break-words font-display text-base font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-462" data-mp="29pjzyrulyr2d">
                        {card.value}
                        {card.href.startsWith('mailto:') ? null : <NewTabHint data-id="element-463" data-mp="36e01lmazsqvu" />}
                      </a>
                    </div>
                  </div>
                </Reveal>)}
            </ul>

            <div className="mt-6 rounded-[20px] border-2 border-brand-navy bg-brand-blue p-5" data-id="element-464" data-mp="1c0qlvcrus955">
              <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-465" data-mp="17zokoqedln0j">Visiting Thursday?</h3>
              <dl className="mt-3 space-y-3" data-id="element-466" data-mp="xvqcdpt0e6jy">
                {visitingDetails.map(item => <div key={item.term} data-id="element-467" data-mp="11wts8cb242l6">
                    <dt className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-468" data-mp="11vs4p8cutnmk">
                      {item.term}
                    </dt>
                    <dd className="mt-0.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-469" data-mp="t3yjh8mdroqn">
                      {item.detail}
                    </dd>
                  </div>)}
              </dl>
              <ButtonExternal href={externalLinks.studentUnion} variant="secondary" className="mt-5 w-full sm:w-auto" data-id="element-470" data-mp="j7n3o8nl0grg">
                Get Directions
              </ButtonExternal>
            </div>
          </div>
        </Container>
      </Section>
    </>;
}